import React, { Component } from "react";
import FormInputs from "../../../components/UI/FormInputs";
import TableGrid from "../../../components/UI/TableGrid";
import { toQueryString, USDateTimeString } from "../../../utils/commonUtils";
import WithRoutify from "../../../hoc/WithRoutify";
import withReduxState from "../../../hoc/wReduxState";
import WithRouter from "../../../hoc/WithRouter";
import OrganizationService from "../../../services/OrganizationService";
import EmployeeMergeService from "../../../services/EmployeeMergeService";
import { success, error } from "../../../store/CommonSlice";
import Alert from "../../../components/UI/ConfirmBox/Alert";
import moment from "moment";

class MergeLogsList extends Component {
  constructor(props) {
    super(props);
    const tableHeaders = [
      {
        label: "Primary Employee",
        key: "primaryEmployee",
        sortName: "created_at",
        sortable: false,
      },
      {
        label: "Secondary Employee",
        key: "secondaryEmployee",
      },
      {
        label: "Merged By",
        key: "mergedBy",
      },
      {
        label: "Date",
        key: "createdAt",
        sortName: "created_at",
        sortable: true,
      },
      {
        label: "Visits",
        key: "visitsTransferred",
        sortName: "visits_transferred",
        sortable: true,
      },
      {
        label: "Status",
        key: "status",
        sortName: "status",
        sortable: true,
      },
      {
        label: "Actions",
        key: "actions",
        type: "Actions",
      },
    ];
    this.state = {
      filters: {
        search_query: "",
        status: "",
      },
      tableRecords: {
        tableHeaders,
        tableRows: {
          status: 1000,
          data: [],
        },
        pagination: {
          page: 1,
          perPage: 25,
          totalRecords: 0,
          sort_by: "created_at",
          sort_order: "desc",
          search: {},
        },
      },
      showRollbackConfirm: false,
      rollbackMergeLogId: null,
    };
  }

  componentDidMount() {
    this.fetchLogList();
  }

  onChangeFiltersHandler = (key, val) => {
    const { filters } = this.state;
    this.setState({ filters: { ...filters, [key]: val } });
  };

  submitFiltersHandler = () => {
    const { filters, tableRecords } = this.state;
    const queryFilters = {
      ...tableRecords.pagination,
      page: 1,
      search: filters,
    };
    this.fetchLogList(queryFilters);
  };

  clearFiltersHandler = () => {
    this.setState({ filters: { search_query: "", status: "" } }, () => {
      this.submitFiltersHandler();
    });
  };

  canRollback = (merge) => {
    if (merge.status !== "completed") return false;
    const hoursSinceMerge = moment().diff(moment(merge.created_at), "hours");
    return hoursSinceMerge <= 24;
  };

  getRollbackTimeLeft = (merge) => {
    if (!merge?.created_at || merge.status !== "completed") return null;
    const expiresAt = moment(merge.created_at).add(24, "hours");
    if (moment().isAfter(expiresAt)) return null;
    const d = moment.duration(expiresAt.diff(moment()));
    const h = Math.floor(d.asHours());
    const m = d.minutes();
    if (h > 0) return `${h}h ${m}m left`;
    return `${m}m left`;
  };

  handleRollbackClick = (mergeLogId) => {
    this.setState({
      showRollbackConfirm: true,
      rollbackMergeLogId: mergeLogId,
    });
  };

  handleRollbackClose = () => {
    this.setState({ showRollbackConfirm: false, rollbackMergeLogId: null });
  };

  handleRollbackConfirm = async () => {
    const { rollbackMergeLogId } = this.state;
    if (!rollbackMergeLogId) return;

    this.handleRollbackClose();
    try {
      this.props.Loader(true);
      const result = await EmployeeMergeService.rollbackMerge(
        null,
        rollbackMergeLogId,
      );
      this.props.dispatch(
        success(result?.message || "Merge rolled back successfully"),
      );
      await this.fetchLogList();
    } catch (err) {
      console.error("Rollback failed", err);
      const msg =
        err?.data?.error || err?.message || "Failed to rollback merge";
      this.props.dispatch(error(msg));
    } finally {
      this.props.Loader(false);
    }
  };

  fetchLogList = async (query = {}) => {
    const { tableRecords } = this.state;
    const orgId =
      OrganizationService.getOrgId() || this.props.profile?.organization_id;
    if (!orgId) {
      this.setState((prev) => ({
        tableRecords: {
          ...prev.tableRecords,
          tableRows: { status: 200, data: [] },
          pagination: {
            ...prev.tableRecords.pagination,
            ...query,
            totalRecords: 0,
          },
        },
      }));
      this.props.dispatch(
        error("Please select an organization to view merge history.")
      );
      return;
    }
    try {
      const queryParams = toQueryString({
        ...tableRecords.pagination,
        ...query,
      });
      const paramsWithOrg = { ...queryParams, orgId };
      this.props.Loader(true);
      const response = await EmployeeMergeService.getMergeLogs(paramsWithOrg);

      if (!response || !response.data) {
        this.setState((prev) => ({
          tableRecords: {
            ...prev.tableRecords,
            tableRows: { status: 200, data: [] },
            pagination: {
              ...prev.tableRecords.pagination,
              ...query,
              totalRecords: 0,
            },
          },
        }));
        return;
      }

      const tableData = (response.data || []).map((merge, idx) => {
        const primary = merge.primary_employee || merge.primaryEmployee;
        const secondary = merge.secondary_employee || merge.secondaryEmployee;
        const mergedBy = merge.merged_by || merge.merged_by_user || merge.mergedBy;
        const primaryName = primary
          ? `${primary.first_name || ""} ${primary.last_name || ""}`.trim()
          : merge.primary_employee_id || "—";
        const secondaryName = secondary
          ? `${secondary.first_name || ""} ${secondary.last_name || ""}`.trim()
          : merge.secondary_employee_id || "—";
        const mergedByName = mergedBy
          ? `${mergedBy.first_name || ""} ${mergedBy.last_name || ""}`.trim()
          : "N/A";
        const statusText = (merge.status || "").replace("_", " ");
        const statusBadge =
          merge.status === "completed"
            ? "bg-success"
            : merge.status === "rolled_back"
              ? "bg-warning"
              : merge.status === "failed"
                ? "bg-danger"
                : "bg-secondary";

        const actions = [];
        if (this.canRollback(merge) && merge.status === "completed") {
          const timeLeft = this.getRollbackTimeLeft(merge);
          actions.push({
            title: "Rollback",
            label: (
              <>
                <i className="fas fa-undo me-1" /> Rollback
                {timeLeft && (
                  <span className="ms-1 small text-white" title="Time remaining to rollback">({timeLeft})</span>
                )}
              </>
            ),
            className: "btn btn-primary ",
            clickHandler: () => this.handleRollbackClick(merge.merge_log_id),
            acl: "any",
          });
        }

        return {
          companyIndex: idx,
          isHeading: false,
          rowId: merge.merge_log_id,
          data: {
            primaryEmployee: {
              value: (
                <div>
                  <div className="merge-refined-name">{primaryName}</div>
                  {merge.merge_reason && (
                    <div className="merge-refined-meta mt-1 small">
                      {merge.merge_reason}
                    </div>
                  )}
                </div>
              ),
            },
            secondaryEmployee: secondaryName,
            mergedBy: mergedByName,
            createdAt: USDateTimeString(merge.created_at),
            visitsTransferred: merge.visits_transferred ?? 0,
            status: {
              value: (
                <span className={`badge ${statusBadge} text-uppercase`}>
                  {statusText}
                </span>
              ),
            },
            actions,
          },
        };
      });

      this.setState((prev) => ({
        tableRecords: {
          ...prev.tableRecords,
          tableHeaders: prev.tableRecords.tableHeaders,
          tableRows: {
            type: "default",
            status: 200,
            data: tableData,
          },
          pagination: {
            ...prev.tableRecords.pagination,
            ...query,
            totalRecords: response.total ?? 0,
          },
        },
      }));
    } catch (err) {
      console.error("Error fetching merge logs", err);
      this.props.dispatch(error("Failed to load merge logs"));
      this.setState((prev) => ({
        tableRecords: {
          ...prev.tableRecords,
          tableRows: { ...prev.tableRecords.tableRows, status: 200 },
        },
      }));
    } finally {
      this.props.Loader(false);
    }
  };

  render() {
    const { tableRecords, filters, showRollbackConfirm } = this.state;

    return (
      <>
        {showRollbackConfirm && (
          <Alert
            type="warning"
            custonTitle="Rollback Merge"
            contentTitle="Are you sure you want to rollback this merge? This will restore the secondary employee record."
            yesButtonName="Rollback"
            noButtonName="Cancel"
            confirmButtonClassName="btn btn-warning"
            onClose={this.handleRollbackClose}
            onDelete={this.handleRollbackConfirm}
          />
        )}

        <div className="sh_cardBox sh_cardbglight">
          <div className="sh_cardHeader">
            <div className="form-group d-flex mb-0 justify-content-between align-items-center formheader flex-nowrap overflow-x-auto">
              <h6 className="text-lg f-500 mb-0 mr-3">Merge History Log</h6>
              <div className="d-flex flex-nowrap align-items-center gap-2 justify-content-end">
                <div
                  className="input-group input-group-joined input-group-solid"
                  style={{ minWidth: "260px" }}
                >
                  <FormInputs
                    placeholder="Search"
                    value={filters.search_query || ""}
                    changeHandler={(val) =>
                      this.onChangeFiltersHandler("search_query", val)
                    }
                    className="form-control form-control-sm"
                  />
                </div>
                <div className="btn-group">
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => this.submitFiltersHandler()}
                  >
                    Search
                  </button>
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => this.clearFiltersHandler()}
                  >
                    Clear
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="sh_cardBody p-0">
            <TableGrid
              hasPagination={true}
              fetchTableRecords={(filters) => this.fetchLogList(filters)}
              gridEditProps={{ activeEditId: null }}
              {...tableRecords}
            />
          </div>
        </div>
      </>
    );
  }
}

export default WithRouter(withReduxState(WithRoutify(MergeLogsList)));
